import React from "react";
import {
  curateOutAnswer,
  curateOutSuccessToast,
  type AnsweredQuestion,
  type AnswerGroup,
  type Citation,
} from "@/api/q-and-a";
import {
  dedupeAnswerGroupsForDisplay,
  filterFoundAnswerGroups,
} from "../utils/answerGroupUtils";
import {
  graphCitationIsGrounded,
  isGraphSourcedAnswer,
} from "../utils/graphCitations";
import { Link } from "react-router-dom";
import { AlertTriangle, BookOpen, SquareArrowUpRight } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { stringToPastelColor } from "@/lib/utils";
import { CitationBlock } from "@/components/shared/answer-citations";
import {
  GraphFullAnswerDialog,
  GraphFullExtractedValue,
} from "@/components/analyses/graph-full-dialog";
import { AnswerFeedbackControls } from "@/components/projects/answer-feedback-controls";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LOW_CONFIDENCE_THRESHOLD } from "@/lib/confidence";

interface AnswerPaneProps {
  questions: AnsweredQuestion[]; // Filtered questions for current document
  selectedQuestionId?: string;
  isVisible: boolean;
  onToggle: () => void;
  onCitationClick: (
    citation: string,
    page: number | null,
    // Omit for page-only navigation (an ungrounded graph source reference that
    // never gets a highlight) — avoids queuing a citation id that never resolves.
    citationId?: string,
  ) => void;
  documentId?: string; // To filter citations and create cross-document links
  target?: {
    instanceId: string;
    groupId?: string;
    citationId?: string;
  };
}

// A numbered green citation pill ("[n] Page x") that scrolls to the citation.
const CitationPill: React.FC<{
  citation: Citation;
  idx: number;
  onCitationClick: AnswerPaneProps["onCitationClick"];
}> = ({ citation, idx, onCitationClick }) => (
  <button
    onClick={() => onCitationClick(citation.citation!, citation.page, citation.id)}
    className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2.5 py-1 text-xs text-green-800 transition-colors hover:bg-green-100"
    title={citation.citation ?? undefined}
  >
    <span className="font-medium">[{idx + 1}]</span>
    {citation.page != null && <span>Page {citation.page}</span>}
  </button>
);

export const AnswerPane: React.FC<AnswerPaneProps> = ({
  questions,
  selectedQuestionId,
  isVisible,
  onToggle,
  onCitationClick,
  documentId,
  target,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const selectedQuestion = selectedQuestionId
    ? questions.find((q) => q.question_id === selectedQuestionId)
    : null;

  const curateFromViewer = async (
    answerId: string | undefined,
    answerGroupId: string,
    reason?: string,
  ) => {
    if (!answerId) return;
    const result = await curateOutAnswer({
      answerId,
      answerGroupId,
      reason,
    });
    await queryClient.invalidateQueries({ queryKey: ["answers"] });
    // The doc viewer reads from a separate cache (useDocumentAnswers, keyed
    // ["document-answers", documentId]) — invalidate it too so the curated-out
    // answer disappears here immediately, not after a stale timeout.
    if (documentId) {
      await queryClient.invalidateQueries({
        queryKey: ["document-answers", documentId],
      });
    }
    toast(curateOutSuccessToast(result));
  };

  // Dedup near-identical answers (incl. graph short + detailed answers for the
  // same question) and carry their citations onto the surviving group. Shared
  // with QuestionPane's group count so both agree.
  const { displayGroups, extraCitationsMap } = React.useMemo(() => {
    if (!selectedQuestion) {
      return {
        displayGroups: [] as AnswerGroup[],
        extraCitationsMap: new Map<string, Citation[]>(),
      };
    }
    return dedupeAnswerGroupsForDisplay(
      filterFoundAnswerGroups(selectedQuestion.answer_groups),
    );
  }, [selectedQuestion]);

  if (!isVisible) {
    return (
      <div className="flex w-8 items-center justify-center border-l border-gray-300 bg-gray-100">
        <button
          onClick={onToggle}
          className="-rotate-90 p-2 text-gray-600 hover:text-gray-800"
          title="Show Answers"
        >
          ▲
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-1/4 flex-col border-l border-gray-300 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-800">Answers</h2>
        <button
          onClick={onToggle}
          className="p-1 text-gray-500 hover:text-gray-700"
          title="Hide Answers"
        >
          ✕
        </button>
      </div>

      {/* Selected Question */}
      {selectedQuestion && (
        <div className="border-b border-gray-200 bg-blue-50 p-4">
          <div className="text-sm font-medium text-gray-800">
            <Link
              to={`/question-bank/?qid=${selectedQuestion.question_id}`}
              className="text-linkblue hover:underline"
              target="_blank"
            >
              {selectedQuestion.question_text}
            </Link>
          </div>
          {selectedQuestion.question_category && (
            <Badge
              className="h-xs mt-1 shrink-0 overflow-ellipsis font-normal"
              style={{
                backgroundColor: stringToPastelColor(
                  selectedQuestion.question_category.id as string,
                ),
              }}
            >
              {selectedQuestion.question_category.name}
            </Badge>
          )}
        </div>
      )}

      {/* Answers List */}
      <div className="flex-1 overflow-y-auto">
        {!selectedQuestion ? (
          <div className="p-4 text-center text-gray-500">
            Select a question to view answers
          </div>
        ) : displayGroups.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No answers found for this question
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {displayGroups.map((group) => {
              // Merge group's own citations with extras aggregated from deduped collapsed groups
              const groupCitations = group.answers.flatMap((a) => a.citations);
              const mergedExtras = extraCitationsMap.get(group.id) ?? [];
              const allCitations = [...groupCitations, ...mergedExtras];

              // Separate citations by document for display
              const { currentDocumentCitations, otherDocumentCitations } = allCitations.reduce(
                (acc, citation) => {
                  if (citation.document_id === documentId) {
                    acc.currentDocumentCitations.push(citation);
                  } else {
                    acc.otherDocumentCitations.push(citation);
                  }
                  return acc;
                },
                { currentDocumentCitations: [] as Citation[], otherDocumentCitations: [] as Citation[] }
              );
              const otherDocumentNames = [...new Set(otherDocumentCitations.map((c) => c.filename))];
              const otherDocumentCitationsCount = otherDocumentCitations.length;

              // Check if any answer in the group is composite
              const hasCompositeAnswer = group.answers.some(
                (answer) => answer.is_composite,
              );

              // Get the first answer for display (or composite answer if exists)
              const displayAnswer = hasCompositeAnswer
                ? group.answers.find((answer) => answer.is_composite)
                : group.answers[0];

              // Feedback/curation in the viewer act on THIS document's answer,
              // not the group's display answer — in a multi-document group the
              // display answer can belong to a different document, so curating
              // it would remove the wrong source. Fall back to the display
              // answer (composite/first) only when no answer cites this doc.
              const docAnswer =
                group.answers.find(
                  (answer) =>
                    !!documentId &&
                    answer.citations?.some(
                      (c) => c.document_id === documentId,
                    ),
                ) ?? displayAnswer;

              const fullMarkdown = displayAnswer?.graph_metadata?.full_markdown;
              // Signature questions encode unsigned status as the
              // sentinel "Not signed" suffix in answer.text. Tint the
              // card so unsigned blocks are visually distinct from
              // signed ones at a glance — diligence reviewers care
              // about which signers haven't signed yet.
              const isUnsignedSignature =
                selectedQuestion?.answer_mode === "signature" &&
                typeof displayAnswer?.text === "string" &&
                displayAnswer.text.includes("Not signed");
              const cardClass = isUnsignedSignature
                ? "border border-red-200 bg-red-50/60 p-4"
                : "border border-gray-200 p-4";

              return (
                <div key={group.id} className={cardClass}>
                  {/* Answer Header */}
                  <div className="mb-2 flex items-start justify-between">
                    {fullMarkdown ? (
                      <div className="flex w-full flex-col gap-1.5">
                        <GraphFullExtractedValue
                          value={displayAnswer?.text}
                          isExtracted={
                            displayAnswer?.graph_metadata?.value_is_extracted
                          }
                        />
                        <GraphFullAnswerTrigger
                          markdown={fullMarkdown}
                          questionText={selectedQuestion?.question_text}
                          detailsHref={`/projects/${target?.instanceId}/q-and-a/?ansid=${group.id}`}
                          references={displayAnswer?.graph_metadata?.references}
                          projectId={target?.instanceId}
                          citations={allCitations}
                          groupId={group.id}
                        />
                      </div>
                    ) : (
                      <>
                        {/* Answer Text - show composite answer if available, otherwise first answer */}
                        <Link
                          to={`/projects/${target?.instanceId}/q-and-a/?ansid=${group.id}`}
                          target="_blank"
                          className="text-linkblue hover:underline"
                          title="See answer details in new window"
                        >
                          {displayAnswer?.text || "No answer available"}

                          <SquareArrowUpRight className="relative inline-block px-1 align-bottom" />
                        </Link>
                        {allCitations.length > 0 && (
                          <span className="whitespace-nowrap text-xs text-gray-500">
                            {allCitations.length} citation
                            {allCitations.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Explanation — skip for graph_full; the dialog IS the answer */}
                  {displayAnswer?.explanation && !fullMarkdown && (
                    <div className="mb-3 text-xs italic text-gray-600">
                      {displayAnswer?.explanation}
                    </div>
                  )}

                  {/* Low Confidence Warning Banner */}
                  {displayAnswer?.grounding_confidence !== null &&
                    displayAnswer?.grounding_confidence !== undefined &&
                    displayAnswer.grounding_confidence <
                      LOW_CONFIDENCE_THRESHOLD && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                              <span>
                                Low confidence answer ({Math.round(displayAnswer.grounding_confidence * 100)}%)
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-xs">
                            <p className="font-medium">Potential issues detected:</p>
                            {displayAnswer.hallucination_flags.length > 0 ? (
                              <ul className="mt-1 list-inside list-disc text-xs">
                                {displayAnswer.hallucination_flags.map((flag: string, i: number) => (
                                  <li key={i}>{flag}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mt-1 text-xs">
                                Some values in this answer may not match the source document.
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                  {/* Citations — graph_full now ships real per-marker
                      citations alongside its placeholder, so this renders
                      both pure text-extracted citations and graph references. */}
                  {allCitations.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {/* Show numbered pills when multiple locations, full blocks when single */}
                      {isGraphSourcedAnswer(displayAnswer) ? (
                        /* Graph / graph_full: the backend grounds each citation
                           to its supporting line (`bbox_grounded`). GROUNDED ->
                           a precise "[n] Page x" pill that scrolls to the
                           highlight. UNGROUNDED (weak/ambiguous match or a "found
                           nothing" answer) -> an honest page-level source
                           reference, no highlight. */
                        <div className="space-y-1">
                          {currentDocumentCitations.map((citation: Citation, idx: number) =>
                            graphCitationIsGrounded(citation) ? (
                              <CitationPill
                                key={citation.id}
                                citation={citation}
                                idx={idx}
                                onCitationClick={onCitationClick}
                              />
                            ) : (
                              <button
                                key={citation.id}
                                // Page-only: this ungrounded graph reference has
                                // no highlight, so navigate to the page without
                                // queuing a citation id that would never resolve.
                                onClick={() =>
                                  onCitationClick(citation.citation ?? "", citation.page)
                                }
                                className="flex w-full flex-col items-start rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-left text-xs text-gray-700 transition-colors hover:bg-gray-100"
                                title="Open source document"
                              >
                                <span className="font-medium">
                                  {citation.filename ?? "Source document"}
                                  {citation.page != null ? ` · Page ${citation.page}` : ""}
                                </span>
                                <span className="text-[10px] text-gray-500">
                                  Graph source. Exact passage not identified.
                                </span>
                              </button>
                            ),
                          )}
                        </div>
                      ) : currentDocumentCitations.length > 1 ? (
                        <>
                          <div className="text-xs text-gray-600">
                            Found in {currentDocumentCitations.length} locations:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {currentDocumentCitations.map((citation: Citation, idx: number) => (
                              <CitationPill
                                key={citation.id}
                                citation={citation}
                                idx={idx}
                                onCitationClick={onCitationClick}
                              />
                            ))}
                          </div>
                        </>
                      ) : (
                        /* Single citation — show full citation block */
                        currentDocumentCitations.map((citation: Citation) => (
                          <CitationBlock
                            key={citation.id}
                            text={citation.citation!}
                            pageNumber={citation.page}
                            onClick={() =>
                              onCitationClick(citation.citation!, citation.page, citation.id)
                            }
                          />
                        ))
                      )}

                      {/* Show other document citations as bullet list */}
                      {otherDocumentCitationsCount > 0 && (
                        <div className="mt-2">
                          <div className="mb-1 text-sm text-gray-600">
                            and {otherDocumentCitationsCount} citation
                            {otherDocumentCitationsCount > 1 ? "s" : ""} in
                            other documents:
                          </div>
                          <ul className="list-inside list-disc space-y-1 pl-2 text-xs text-gray-600">
                            {otherDocumentNames.map((documentName) => (
                              <li key={documentName}>{documentName}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Flags and Status */}
                  <div className="mt-3 flex items-center justify-between text-xs">
                    {hasCompositeAnswer && (
                      <span className="rounded bg-yellow-100 px-2 py-1 text-yellow-800">
                        Composite Answer
                      </span>
                    )}
                    {group.answers.some((answer) => answer.flagged) && (
                      <span className="rounded bg-red-100 px-2 py-1 text-red-800">
                        Flagged
                      </span>
                    )}
                    {group.is_current_value && (
                      <span className="rounded bg-green-100 px-2 py-1 text-green-800">
                        Current Value
                      </span>
                    )}
                    <span className="text-gray-500">
                      {displayAnswer &&
                        new Date(displayAnswer.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Feedback controls — same correct/incorrect/unsure
                      pills + comment popover as the Q&A panel and report
                      cells, so a reviewer reading the source PDF can
                      label the extraction without leaving the document. */}
                  {selectedQuestion && (
                    <div className="mt-3 border-t border-gray-100 pt-2">
                      <AnswerFeedbackControls
                        questionId={selectedQuestion.question_id}
                        answerGroupId={group.id}
                        answerId={docAnswer?.id}
                        variant="inline"
                        curateOnIncorrect
                        onCurateOut={(reason) =>
                          curateFromViewer(docAnswer?.id, group.id, reason)
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};


/** Compact trigger rendered in the doc viewer answer card for graph_full
 * answers. The long markdown lives behind a dialog rather than being
 * inlined in the narrow side pane. */
const GraphFullAnswerTrigger: React.FC<{
  markdown: string;
  questionText?: string;
  detailsHref?: string;
  references?: Array<{
    marker: number;
    document_id: string;
    filename?: string | null;
    page?: number | null;
  }>;
  projectId?: string;
  citations?: Citation[];
  groupId?: string;
}> = ({ markdown, questionText, detailsHref, references, projectId, citations, groupId }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 transition-colors hover:border-blue-200 hover:bg-blue-100 hover:text-blue-900"
      >
        <BookOpen size={12} />
        View Answer
      </button>
      {detailsHref && (
        <Link
          to={detailsHref}
          target="_blank"
          className="text-linkblue hover:underline"
          title="See answer details in new window"
        >
          <SquareArrowUpRight className="h-4 w-4" />
        </Link>
      )}
      <GraphFullAnswerDialog
        open={open}
        onOpenChange={setOpen}
        questionText={questionText}
        markdown={markdown}
        references={references}
        projectId={projectId}
        citations={citations}
        groupId={groupId}
      />
    </div>
  );
};
